import React, { useEffect, useState } from 'react';
import './FadeInImage.css';

/**
 * Reusable image that preloads and fades in once ready.
 * `as="div"` renders a background image; default renders an <img>.
 */
function FadeInImage({
	src,
	alt = '',
	className = '',
	as = 'img',
	style = {},
	backgroundSize = 'cover',
	backgroundPosition = 'center',
	backgroundRepeat = 'no-repeat',
	...rest
}) {
	const [loaded, setLoaded] = useState(false);

	useEffect(() => {
		if (!src) return undefined;

		let isMounted = true;
		setLoaded(false);
		const img = new Image();
		img.loading = 'eager';
		if (img.decode) {
			img.decoding = 'async';
		}
		img.src = src;

		const markLoaded = () => {
			if (!isMounted) return;
			if (img.decode) {
				img
					.decode()
					.then(() => isMounted && setLoaded(true))
					.catch(() => isMounted && setLoaded(true));
				return;
			}
			setLoaded(true);
		};

		if (img.complete) {
			markLoaded();
		} else {
			img.addEventListener('load', markLoaded);
			img.addEventListener('error', markLoaded);
		}

		return () => {
			isMounted = false;
			img.removeEventListener('load', markLoaded);
			img.removeEventListener('error', markLoaded);
		};
	}, [src]);

	if (as === 'div') {
		const backgroundStyle = loaded ? { backgroundImage: `url('${src}')` } : {};
		return (
			<div
				className={`fade-image ${loaded ? 'isLoaded' : 'isLoading'} ${className}`}
				style={{
					backgroundSize,
					backgroundPosition,
					backgroundRepeat,
					visibility: loaded ? 'visible' : 'hidden',
					...style,
					...backgroundStyle,
				}}
				aria-hidden
				{...rest}
			/>
		);
	}

	return (
		<img
			src={src}
			alt={alt}
			className={`fade-image ${loaded ? 'isLoaded' : 'isLoading'} ${className}`}
			style={{
				visibility: loaded ? 'visible' : 'hidden',
				...style,
			}}
			loading='eager'
			{...rest}
		/>
	);
}

export default FadeInImage;
